import { z } from "zod";
import { errorCodeSchema } from "./errors.js";
import { particleSchema } from "./particle.js";
import { showConfigSchema } from "./config.js";
import { snapshotSchema } from "./snapshot.js";

export const serverMessageSchema = z.discriminatedUnion("type", [
  /** Always the first frame after a successful `hello`. */
  z.object({
    type: z.literal("snapshot"),
    snapshot: snapshotSchema,
  }),

  /**
   * One or more new particles. Always an array, even for a single spawn: one code
   * path on both ends, and batching comes for free.
   */
  z.object({
    type: z.literal("particles_spawned"),
    /** Highest seq in this batch. */
    seq: z.number().int(),
    particles: z.array(particleSchema).min(1),
    totalParticles: z.number().int(),
    /** Server epoch ms at fan-out; the client measures render latency against it. */
    broadcastAt: z.number().int(),
  }),

  z.object({
    type: z.literal("config_updated"),
    seq: z.number().int(),
    config: showConfigSchema,
  }),

  z.object({
    type: z.literal("pattern_triggered"),
    seq: z.number().int(),
    round: z.number().int(),
    startedAt: z.number().int(),
    durationMs: z.number().int(),
    particleCount: z.number().int(),
  }),

  /** The animation is over. The stage is now `completed` and holds until an admin reset. */
  z.object({
    type: z.literal("pattern_completed"),
    seq: z.number().int(),
    round: z.number().int(),
    completedAt: z.number().int(),
  }),

  /** Snapshot is inlined so there is no window where a client saw the reset but not the new state. */
  z.object({
    type: z.literal("state_reset"),
    seq: z.number().int(),
    resetCount: z.number().int(),
    snapshot: snapshotSchema,
  }),

  /** Liveness; echoed back as `pong` with the same `t`. */
  z.object({ type: z.literal("ping"), t: z.number().int() }),

  z.object({
    type: z.literal("error"),
    code: errorCodeSchema,
    message: z.string(),
    /** true = do not retry with the same parameters (retrying would just loop). */
    fatal: z.boolean(),
  }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;
export type ServerMessageOf<T extends ServerMessage["type"]> = Extract<ServerMessage, { type: T }>;
